import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { FLOWS_ENUM } from '~/common/stores/ui/loginStore';
import InputField from '~/common/components/InputField';
import Button from '~/common/components/CommonButton';

// Import Local Styles
import styles from './LoginModal.css';
import LoadingSpinner from '../LoadingSpinner';

@inject('annualUpgradeStore', 'formsStore', 'convertToPaidStore', 'rubyStore', 'loginStore')
@observer
export default class LoginModal extends Component {
    state = {
        // Verification code inputs
        codes: {
            authCode0: '',
            authCode1: '',
            authCode2: '',
            authCode3: '',
            authCode4: '',
            authCode5: ''
        },
        authCodeEntered: false,
        time: {},
        seconds: 0,
        showTimer: false,
        showResendCta: false
    };

    constructor(props) {
        super(props);
        this.inputRefs = [];
        this.timer = 0;
        this.setRef = this.setRef.bind(this);
        this.startTimer = this.startTimer.bind(this);
        this.countDown = this.countDown.bind(this);
        this.secondsToTime = this.secondsToTime.bind(this);
        this.resetCode = this.resetCode.bind(this);
    }

    componentDidUpdate() {
        if (this.props.loginStore.showVerification && this.props.loginStore.resendInterval > 0 && !this.state.showTimer) {
            this.setState({ seconds: this.props.loginStore.resendInterval });
            this.setState({ showTimer: true });
            this.startTimer();
        }

        if (!this.props.loginStore.showVerification && this.state.showTimer) {
            this.resetCode();
        }
    }

    // Sets ref to the verification code inputs
    setRef(input) {
        this.inputRefs.push(input);
    }

    secondsToTime(secs) {
        const divisorForMinutes = secs % (60 * 60);
        const minutes = Math.floor(divisorForMinutes / 60);

        const divisorForSeconds = divisorForMinutes % 60;
        const seconds = Math.ceil(divisorForSeconds);
        return {
            m: minutes,
            s: seconds
        };
    }

    startTimer() {
        if (!this.timer) {
            this.timer = setInterval(this.countDown, 1000);
        }
    }

    countDown() {
        // Remove one second, set state so a re-render happens.
        const seconds = this.state.seconds - 1;
        this.setState({
            time: this.secondsToTime(seconds),
            seconds
        });

        // Check if we're at zero.
        if (seconds === 0) {
            clearInterval(this.timer);
            this.timer = 0;
            this.setState({ showResendCta: true });
        }
    }

    // Set verification code
    setCode(key, event, index) {
        this.props.loginStore.setAuthError('');
        const codes = this.state.codes;
        const value = event.target.value;
        codes[key] = value;
        let authCodeEntered = true;

        Object.values(codes).forEach((code) => {
            if (!code) {
                authCodeEntered = false;
            }
        });

        if (authCodeEntered) {
            this.setState({ authCodeEntered });
        }

        this.setState(codes);

        if (value) {
            const nextInput = this.inputRefs[index + 1];
            if (nextInput) {
                nextInput.focus();
                nextInput.select();
            }
        }
    }

    // Submit Verification code
    submitVerificationCode() {
        const { convertToPaidStore, rubyStore, loginStore, annualUpgradeStore } = this.props;
        const { logInFlow } = loginStore;

        const codes = this.state.codes;
        const code = Object.values(codes).reduce((acc, nex) => acc + nex);

        switch (logInFlow) {
            case FLOWS_ENUM.RUBY:
                rubyStore.validateUser(code);
                break;
            case FLOWS_ENUM.ANNUAL_UPGRADE:
                annualUpgradeStore.validateUser(code);
                break;
            default:
                // legacy
                convertToPaidStore.validateUser(code);
                break;
        }
    }

    // Paste copied verification code into the inputs
    pasteCodeHandler(event) {
        const pastedCode = event?.clipboardData?.getData('text');
        if (pastedCode && pastedCode.length === 6 && pastedCode.match(/^[a-zA-Z0-9]*$/)) {
            const { codes } = this.state;
            Object.keys(codes).forEach((key, i) => {
                codes[key] = pastedCode[i];
            });
            this.setState({ authCodeEntered: true });
            this.setState(codes);
        }
    }

    resetCode() {
        const { codes } = this.state;
        Object.keys(codes).forEach((key) => {
            codes[key] = '';
        });
        this.setState({ authCodeEntered: false });
        this.setState(codes);
        clearInterval(this.timer);
        this.timer = 0;
        this.setState({ showTimer: false });
        this.setState({ time: {} });
        this.setState({ showResendCta: false });
    }


    // Remove current code and focus to previous code input for backspace/delete keydown
    deleteCodeHandler(event, inputIndex) {
        const keyID = event.keyCode;
        if (keyID === 46 || keyID === 8) {
            this.setState({ authCodeEntered: false });
            if (!event.target.value) {
                const previousInput = this.inputRefs[inputIndex - 1];

                event.preventDefault();

                if (previousInput) {
                    previousInput.focus();
                }
            }
        }
    }

    submitForm(ev) {
        ev.preventDefault();

        const { formsStore, convertToPaidStore, rubyStore, loginStore, annualUpgradeStore } = this.props;
        const { logInFlow, loginByUsername } = loginStore;
        const fields = ['PasswordLogin'];
        const loginMethod = loginByUsername ? 'Username' : 'Email';


        fields.push(loginMethod);

        // clear previous log in error
        loginStore.setLoginError('');

        // run validation on all fields
        formsStore.validateForm(fields);
        // check if any error exist from validation
        if (!(formsStore.doesFormHaveErrors(fields))) {
            switch (logInFlow) {
                case FLOWS_ENUM.RUBY:
                    rubyStore.authUser();
                    break;
                case FLOWS_ENUM.ANNUAL_UPGRADE:
                    annualUpgradeStore.authUser();
                    break;
                default:
                    // legacy
                    convertToPaidStore.authUser();
                    break;
            }
        }
    }

    render() {
        const {
            Email,
            EmailError,
            PasswordLogin,
            PasswordLoginError,
            Username,
            UsernameError
        } = this.props.formsStore;

        const { canTrialBeConverted } = this.props.convertToPaidStore;
        const { loginError, loginByUsername, isRubyLogInFlow, maskedEmail, showVerification, authError, authLoading } = this.props.loginStore;
        const { codes, authCodeEntered, time, showResendCta } = this.state;

        return (
            <div className={styles.wrapper} id="trial__login-modal">
                {!showVerification ?
                    <Fragment>
                        <h3 className={styles.header}>{`Log in to your${isRubyLogInFlow ? ' Grasshopper ' : ' '}account`}</h3>
                        <form className={styles.form}>
                            {loginError && <div className={styles.error} id="login-modal__error">{loginError}</div>}
                            <Fragment>
                                {loginByUsername ?
                                    <InputField
                                        allowedCharacters="@.!#$%&'*+-/=?^_`{|}:~"
                                        autoComplete="email"
                                        defaultValue={Username}
                                        disabled={!canTrialBeConverted}
                                        error={UsernameError}
                                        id="login__username"
                                        label="Username / Email"
                                        name="Username"
                                        restrictCharacters="alphaNumericOnly"
                                        type="text"
                                    />
                                    :
                                    // Email
                                    <InputField
                                        allowedCharacters="@.!#$%&'*+-/=?^_`{|}~"
                                        autoComplete="email"
                                        defaultValue={Email}
                                        disabled={!canTrialBeConverted}
                                        error={EmailError}
                                        id="login__email"
                                        label="Email"
                                        name="Email"
                                        restrictCharacters="alphaNumericOnly"
                                        type="email"
                                    />
                                }
                                {
                                // Password
                                }
                                <InputField
                                    allowedCharacters="@.!#$%&'*+-/=?^_`{|}~"
                                    autoComplete="password"
                                    defaultValue={PasswordLogin}
                                    disabled={!canTrialBeConverted}
                                    error={PasswordLoginError}
                                    id="login__password"
                                    label="Password"
                                    name="PasswordLogin"
                                    restrictCharacters="alphaNumericOnly"
                                    type="password"
                                />
                                <Button
                                    disabled={!canTrialBeConverted}
                                    modifier={styles.button}
                                    onClick={ev => this.submitForm(ev)}
                                >
                                        Log in
                                </Button>
                            </Fragment>
                        </form>
                        <a
                            className={styles.link}
                            href="https://portal.us.grasshopper.com/ForgotPassword.aspx"
                            id="forgot-password"
                            target="_blank"
                        >
                            Forgot your password?
                        </a>
                    </Fragment>
                    :
                    <Fragment>
                        <div className={styles.auth}>
                            <div className={styles['auth-headers']}>
                                <Button
                                    modifier={styles['auth-header-btn']} onClick={() => {
                                        this.props.loginStore.setShowVerification(false);
                                        this.props.loginStore.setAuthError('');
                                        this.props.loginStore.setResendInterval(0);
                                        this.setState({ showResendCta: false });
                                    }}
                                    tertiary
                                >Back</Button>
                                <h3 className={styles['auth-header']}>Code confirmation</h3>
                                <p className={styles['auth-text']}>We sent a code to {maskedEmail}</p>
                            </div>
                            <div>
                                <div className={styles['auth-code-block']}>
                                    {[0, 1, 2, 3, 4, 5].map(index => (
                                        <InputField
                                            defaultValue={codes[`authCode${index}`]}
                                            id={`auth-code${index}`}
                                            label=""
                                            maxLength={1}
                                            modifier={`${styles['auth-code']} ${codes[`authCode${index}`] ? styles['auth-code--checked'] : ''} ${authError ? styles['auth-code--error'] : ''}`}
                                            name={`AuthCode${index}`}
                                            onChange={event => this.setCode(`authCode${index}`, event, index)}
                                            onKeyDown={event => this.deleteCodeHandler(event, index)}
                                            onPaste={event => this.pasteCodeHandler(event)}
                                            restrictCharacters="alphaNumericOnly"
                                            setRef={this.setRef}
                                            wrapperClass={styles['auth-code-wrapper']}
                                        />
                                    ))}
                                </div>
                                {authError && <p className={styles['auth-error']}>{authError}</p>}
                            </div>
                            <Button
                                disabled={!authCodeEntered || authError}
                                modifier={`${styles['auth-submit']} ${!authCodeEntered ? styles['auth-submit--disabled'] : ''} ${authLoading ? `${styles['auth-submit--loading']} ${styles['auth-submit--disabled']}` : ''}`}
                                onClick={() => this.submitVerificationCode()}
                            >
                                {authLoading ? <LoadingSpinner className={styles['auth-submit-spinner']} showDotLoader /> : 'Verify'}
                            </Button>
                            <p className={styles['auth-resend']}>
                                    Didn’t receive the email?
                                {showResendCta ?
                                    <Button
                                        modifier={styles['auth-resend-cta']} onClick={() => {
                                            this.props.loginStore.getNewVerificationCode();
                                            this.setState({ showResendCta: false });
                                            this.setState({ showTimer: false });
                                        }}
                                        tertiary
                                    >Resend code</Button>
                                    : <span className={styles['auth-resend-text']}> Resend code in <span className={styles['auth-resend-time']}> {time.m > 0 ? `0${time.m}` : '00'}:{time.s < 10 ? `0${time.s}` : time.s || '00'}</span>
                                    </span>
                                }
                            </p>
                        </div>
                    </Fragment>
                }
            </div>
        );
    }
}

